/**
 * Resolves one attachment API path into a signed blob URL for an element's src.
 *
 * Returns `null` until the mint completes, so callers render their existing
 * placeholder in the meantime. `onError` is meant for the element's own error
 * handler: a URL that expired while the page sat open is re-minted once, which
 * turns a stale signature into a refresh rather than a broken image.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { forgetAttachmentUrl, resolveAttachmentUrl } from "@/lib/attachmentUrls";

interface AttachmentUrl {
  url: string | null;
  onError: () => void;
}

export function useAttachmentUrl(path: string | null | undefined): AttachmentUrl {
  const [url, setUrl] = useState<string | null>(null);
  // One retry only. Without this an element whose src is genuinely broken would
  // re-mint on every error event, in a loop.
  const retried = useRef(false);

  useEffect(() => {
    retried.current = false;

    if (!path) {
      setUrl(null);
      return;
    }

    let active = true;
    resolveAttachmentUrl(path)
      .then((resolved) => {
        if (active) setUrl(resolved);
      })
      .catch((error) => {
        console.error("Attachment URL mint failed", path, error);
      });

    return () => {
      active = false;
    };
  }, [path]);

  const onError = useCallback(() => {
    if (!path || retried.current) return;
    retried.current = true;
    forgetAttachmentUrl(path);
    resolveAttachmentUrl(path)
      .then(setUrl)
      .catch((error) => {
        console.error("Attachment URL re-mint failed", path, error);
      });
  }, [path]);

  return { url, onError };
}
